"use strict";
var _a, _b, _c, _d, _e, _f, _g;
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashboardService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const appointment_entity_1 = require("../consultation/appointment.entity");
const customer_entity_1 = require("../customers/customer.entity");
const interaction_log_entity_1 = require("../customers/interaction-log.entity");
const inventory_stock_entity_1 = require("../inventory/inventory-stock.entity");
const order_entity_1 = require("../orders/order.entity");
const service_ticket_entity_1 = require("../tickets/service-ticket.entity");
const ticket_evidence_entity_1 = require("../tickets/ticket-evidence.entity");
let DashboardService = class DashboardService {
    constructor(orderRepo, ticketRepo, evidenceLinkRepo, stockRepo, customerRepo, appointmentRepo, interactionLogRepo) {
        this.orderRepo = orderRepo;
        this.ticketRepo = ticketRepo;
        this.evidenceLinkRepo = evidenceLinkRepo;
        this.stockRepo = stockRepo;
        this.customerRepo = customerRepo;
        this.appointmentRepo = appointmentRepo;
        this.interactionLogRepo = interactionLogRepo;
    }
    async getSummary() {
        const now = new Date();
        const startToday = this.startOfDay(now);
        const endToday = this.endOfDay(now);
        const startYesterday = this.startOfDay(this.addDays(now, -1));
        const endYesterday = this.endOfDay(this.addDays(now, -1));
        const startCurrentWeek = this.startOfDay(this.addDays(now, -6));
        const startPreviousWeek = this.startOfDay(this.addDays(now, -13));
        const endPreviousWeek = this.endOfDay(this.addDays(now, -7));
        const trajectoryDays = this.getLastSevenDays(now);
        const [todayOrders, yesterdayOrders, todayRevenueRaw, currentWeekRevenueRaw, previousWeekRevenueRaw, openTickets, escalatedTickets, lowStockRows, recentOrders, recentTickets,] = await Promise.all([
            this.orderRepo.count({ where: { createdAt: (0, typeorm_2.Between)(startToday, endToday) } }),
            this.orderRepo.count({ where: { createdAt: (0, typeorm_2.Between)(startYesterday, endYesterday) } }),
            this.sumOrderRevenue(startToday, endToday),
            this.sumOrderRevenue(startCurrentWeek, endToday),
            this.sumOrderRevenue(startPreviousWeek, endPreviousWeek),
            this.ticketRepo.count({
                where: { status: (0, typeorm_2.In)([service_ticket_entity_1.TicketStatus.OPEN, service_ticket_entity_1.TicketStatus.IN_PROGRESS, service_ticket_entity_1.TicketStatus.ESCALATED]) },
            }),
            this.ticketRepo.count({ where: { status: service_ticket_entity_1.TicketStatus.ESCALATED } }),
            this.stockRepo.find({ relations: ['product'] }),
            this.orderRepo.find({
                relations: ['customer'],
                order: { createdAt: 'DESC' },
                take: 4,
            }),
            this.ticketRepo.find({
                where: { status: (0, typeorm_2.In)([service_ticket_entity_1.TicketStatus.OPEN, service_ticket_entity_1.TicketStatus.IN_PROGRESS, service_ticket_entity_1.TicketStatus.ESCALATED]) },
                relations: ['assignedTo'],
                order: { createdAt: 'DESC' },
                take: 4,
            }),
        ]);
        const orderTrajectory = await Promise.all(trajectoryDays.map(async ({ label, start, end }) => ({
            label,
            orders: await this.orderRepo.count({ where: { createdAt: (0, typeorm_2.Between)(start, end) } }),
            revenue: await this.sumOrderRevenue(start, end),
        })));
        const lowStock = lowStockRows.filter((row) => {
            const available = Number(row.quantityOnHand) - Number(row.quantityReserved);
            return Number(row.reorderLevel) > 0 && available <= Number(row.reorderLevel);
        });
        const criticalLowStock = lowStock.filter((row) => {
            const available = Number(row.quantityOnHand) - Number(row.quantityReserved);
            return available <= 0;
        });
        const uniqueLowStockSkus = new Set(lowStock.map((row) => row.product?.id).filter(Boolean)).size;
        const uniqueCriticalSkus = new Set(criticalLowStock.map((row) => row.product?.id).filter(Boolean)).size;
        return {
            todayOrders,
            yesterdayOrders,
            todayRevenue: todayRevenueRaw,
            currentWeekRevenue: currentWeekRevenueRaw,
            previousWeekRevenue: previousWeekRevenueRaw,
            openTickets,
            escalatedTickets,
            lowStockSkus: uniqueLowStockSkus,
            criticalLowStockSkus: uniqueCriticalSkus,
            orderTrajectory,
            recentOrders: recentOrders.map((order) => ({
                id: order.orderNumber,
                customer: order.customer?.name ?? 'Walk-in customer',
                amount: Number(order.totalAmount),
                status: order.status,
            })),
            recentTickets: recentTickets.map((ticket) => ({
                id: ticket.ticketNumber,
                topic: this.humanizeTicketType(ticket.ticketType),
                queue: ticket.assignedTo?.name ?? this.defaultQueue(ticket.ticketType),
                ageMinutes: Math.max(1, Math.round((now.getTime() - new Date(ticket.createdAt).getTime()) / 60000)),
            })),
        };
    }
    async getCallCenterSummary(agentId) {
        const now = new Date();
        const startToday = this.startOfDay(now);
        const endToday = this.endOfDay(now);
        const nextTwoDays = this.endOfDay(this.addDays(now, 2));
        const [callsToday, customersHelpedRaw, openTickets, appointmentsBooked, priorityFollowUps, recentInteractions, recentCustomers, recentOrders, appointmentActions, evidenceReadyLinks, agentIssues,] = await Promise.all([
            this.interactionLogRepo.count({
                where: {
                    agent: { id: agentId },
                    type: (0, typeorm_2.In)([interaction_log_entity_1.LogType.INBOUND_CALL, interaction_log_entity_1.LogType.OUTBOUND_CALL]),
                    createdAt: (0, typeorm_2.Between)(startToday, endToday),
                },
            }),
            this.interactionLogRepo
                .createQueryBuilder('log')
                .select('COUNT(DISTINCT log.customerId)', 'count')
                .where('log.agentId = :agentId', { agentId })
                .andWhere('log.createdAt BETWEEN :start AND :end', { start: startToday, end: endToday })
                .getRawOne(),
            this.ticketRepo.count({
                where: { status: (0, typeorm_2.In)([service_ticket_entity_1.TicketStatus.OPEN, service_ticket_entity_1.TicketStatus.IN_PROGRESS, service_ticket_entity_1.TicketStatus.ESCALATED]) },
            }),
            this.appointmentRepo.count({
                where: {
                    bookedByAgent: { id: agentId },
                    createdAt: (0, typeorm_2.Between)(startToday, endToday),
                },
            }),
            this.ticketRepo.find({
                where: { status: (0, typeorm_2.In)([service_ticket_entity_1.TicketStatus.OPEN, service_ticket_entity_1.TicketStatus.IN_PROGRESS, service_ticket_entity_1.TicketStatus.ESCALATED]) },
                relations: ['customer'],
                order: { updatedAt: 'DESC' },
                take: 4,
            }),
            this.interactionLogRepo.find({
                relations: ['customer', 'agent'],
                order: { createdAt: 'DESC' },
                take: 5,
            }),
            this.customerRepo.find({
                order: { createdAt: 'DESC' },
                take: 5,
            }),
            this.orderRepo.find({
                relations: ['customer'],
                order: { createdAt: 'DESC' },
                take: 5,
            }),
            this.appointmentRepo.find({
                where: {
                    status: (0, typeorm_2.In)([appointment_entity_1.AppointmentStatus.SCHEDULED, appointment_entity_1.AppointmentStatus.RESCHEDULED, appointment_entity_1.AppointmentStatus.CONFIRMED]),
                    scheduledAt: (0, typeorm_2.Between)(startToday, nextTwoDays),
                },
                relations: ['customer', 'doctor', 'bookedByAgent'],
                order: { scheduledAt: 'ASC' },
                take: 5,
            }),
            this.evidenceLinkRepo.find({
                where: { uploadComplete: true },
                relations: ['ticket', 'ticket.customer', 'ticket.relatedOrder'],
                order: { updatedAt: 'DESC' },
                take: 8,
            }),
            this.ticketRepo.find({
                where: { createdBy: { id: agentId } },
                relations: ['customer', 'relatedOrder'],
                order: { updatedAt: 'DESC' },
                take: 50,
            }),
        ]);
        const customersHelped = Number(customersHelpedRaw?.count ?? 0);
        const escalatedOrUrgent = priorityFollowUps.filter((ticket) => ticket.status === service_ticket_entity_1.TicketStatus.ESCALATED || [service_ticket_entity_1.TicketPriority.HIGH, service_ticket_entity_1.TicketPriority.URGENT].includes(ticket.priority));
        const evidenceReadyIssues = evidenceReadyLinks
            .filter((link) => {
            const ticket = link.ticket;
            if (!ticket || !Array.isArray(link.files) || link.files.length === 0)
                return false;
            return [service_ticket_entity_1.TicketType.LOGISTICS_DAMAGED, service_ticket_entity_1.TicketType.DAMAGED_ORDER].includes(ticket.ticketType)
                && ticket.workflowStage === service_ticket_entity_1.WorkflowStage.EVIDENCE_LINK_SENT
                && !ticket.claimVerified;
        })
            .map((link) => ({
            id: link.ticket.ticketNumber,
            ticketId: link.ticket.id,
            customerId: link.ticket.customer?.id ?? null,
            customer: link.ticket.customer?.name ?? 'Customer',
            topic: this.humanizeTicketType(link.ticket.ticketType),
            priority: link.ticket.priority,
            relatedOrder: link.ticket.relatedOrder?.orderNumber ?? null,
            fileCount: link.files.length,
            submittedAt: link.updatedAt,
        }));
        const evidenceReadyTicketIds = new Set(evidenceReadyIssues.map((item) => item.ticketId));
        return {
            stats: {
                callsToday,
                customersHelped,
                openTickets,
                appointmentsBooked,
            },
            queues: {
                followUpsDue: priorityFollowUps.length,
                pendingIssues: escalatedOrUrgent.length,
                appointmentActions: appointmentActions.length,
                evidenceReady: evidenceReadyIssues.length,
            },
            evidenceReadyIssues,
            agentIssues: agentIssues.map((ticket) => ({
                id: ticket.ticketNumber,
                ticketId: ticket.id,
                customerId: ticket.customer?.id ?? null,
                customer: ticket.customer?.name ?? 'Customer',
                topic: this.humanizeTicketType(ticket.ticketType),
                status: ticket.status,
                displayStatus: evidenceReadyTicketIds.has(ticket.id) ? 'evidence_uploaded' : ticket.status,
                priority: ticket.priority,
                workflowStage: ticket.workflowStage,
                relatedOrder: ticket.relatedOrder?.orderNumber ?? null,
                updatedAt: ticket.updatedAt,
            })),
            priorityFollowUps: priorityFollowUps.map((ticket) => ({
                id: ticket.ticketNumber,
                customer: ticket.customer?.name ?? 'Customer',
                topic: this.humanizeTicketType(ticket.ticketType),
                status: ticket.status,
                priority: ticket.priority,
            })),
            recentInteractions: recentInteractions.map((log) => ({
                id: log.id,
                customer: log.customer?.name ?? 'Customer',
                type: log.type,
                description: log.description,
                createdAt: log.createdAt,
            })),
            appointmentActions: appointmentActions.map((appointment) => ({
                id: appointment.id,
                customer: appointment.customer?.name ?? 'Patient',
                doctor: appointment.doctor?.name ?? 'Doctor',
                consultationType: appointment.consultationType,
                status: appointment.status,
                scheduledAt: appointment.scheduledAt,
            })),
            recentCustomers: recentCustomers.map((customer) => ({
                id: customer.id,
                name: customer.name,
                patientId: customer.patientId,
                phone: customer.phone,
                patientType: customer.patientType,
            })),
            recentOrders: recentOrders.map((order) => ({
                id: order.id,
                orderNumber: order.orderNumber,
                customer: order.customer?.name ?? 'Customer',
                status: order.status,
                amount: Number(order.totalAmount),
            })),
        };
    }
    async sumOrderRevenue(start, end) {
        const result = await this.orderRepo
            .createQueryBuilder('order')
            .select('COALESCE(SUM(order.totalAmount), 0)', 'total')
            .where('order.createdAt BETWEEN :start AND :end', { start, end })
            .getRawOne();
        return Number(result?.total ?? 0);
    }
    startOfDay(date) {
        const value = new Date(date);
        value.setHours(0, 0, 0, 0);
        return value;
    }
    endOfDay(date) {
        const value = new Date(date);
        value.setHours(23, 59, 59, 999);
        return value;
    }
    addDays(date, days) {
        const value = new Date(date);
        value.setDate(value.getDate() + days);
        return value;
    }
    getLastSevenDays(now) {
        return Array.from({ length: 7 }, (_, index) => {
            const day = this.addDays(now, index - 6);
            return {
                label: day.toLocaleString('en-IN', { weekday: 'short' }),
                start: this.startOfDay(day),
                end: this.endOfDay(day),
            };
        });
    }
    humanizeTicketType(type) {
        return type
            .split('_')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }
    defaultQueue(type) {
        if (type.includes('refund'))
            return 'Finance';
        if (type.includes('consult'))
            return 'Medical Ops';
        if (type.includes('logistics') || type.includes('return'))
            return 'Logistics';
        return 'Support';
    }
};
exports.DashboardService = DashboardService;
exports.DashboardService = DashboardService = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(order_entity_1.Order)),
    tslib_1.__param(1, (0, typeorm_1.InjectRepository)(service_ticket_entity_1.ServiceTicket)),
    tslib_1.__param(2, (0, typeorm_1.InjectRepository)(ticket_evidence_entity_1.TicketEvidenceLink)),
    tslib_1.__param(3, (0, typeorm_1.InjectRepository)(inventory_stock_entity_1.InventoryStock)),
    tslib_1.__param(4, (0, typeorm_1.InjectRepository)(customer_entity_1.Customer)),
    tslib_1.__param(5, (0, typeorm_1.InjectRepository)(appointment_entity_1.Appointment)),
    tslib_1.__param(6, (0, typeorm_1.InjectRepository)(interaction_log_entity_1.InteractionLog)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _b : Object, typeof (_c = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _c : Object, typeof (_d = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _d : Object, typeof (_e = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _e : Object, typeof (_f = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _f : Object, typeof (_g = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _g : Object])
], DashboardService);
